import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { ChannelItem, SyndicateItemParams } from 'types/channel';
import { useDialogFocus } from '../../../../hooks/useDialogFocus';
import {
  SyndicationCandidateChannel,
  useSyndicateItem,
} from '../../../../hooks/useSyndicateItem';
import { getLocalizedText } from '../../../../utils/getLocalizedText';
import './SyndicateItemDialogOrganism.scss';

export interface SyndicateItemDialogOrganismProps {
  channelId: string;
  visible: boolean;
  onHide: () => void;
  onSyndicated: (item: ChannelItem) => void;
}

type VersionMode = 'pinned' | 'latest';
type StepIndex = 0 | 1 | 2;

const SEARCH_DEBOUNCE_MS = 300;

const SyndicateItemDialogOrganism: React.FC<SyndicateItemDialogOrganismProps> = ({
  channelId,
  visible,
  onHide,
  onSyndicated,
}) => {
  const { t } = useTranslation();
  const {
    channels,
    items,
    selectedChannel,
    selectedItem,
    loading,
    error,
    searchChannels,
    selectChannel,
    selectItem,
    syndicate,
    reset,
  } = useSyndicateItem(channelId);

  const [stepIndex, setStepIndex] = useState<StepIndex>(0);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [versionMode, setVersionMode] = useState<VersionMode>('latest');
  const [pinnedVersion, setPinnedVersion] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogFocus(visible, dialogRef);

  useEffect(() => {
    if (!visible) return;
    reset();
    setStepIndex(0);
    setSearchInput('');
    setDebouncedSearch('');
    setVersionMode('latest');
    setPinnedVersion(null);
    setSubmitting(false);
    setSubmitError(null);
  }, [visible, reset]);

  useEffect(() => {
    if (!visible) return;
    searchChannels(debouncedSearch);
  }, [visible, debouncedSearch, searchChannels]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchInput]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onHide();
  }, [submitting, onHide]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleSelectChannel = useCallback(
    (channel: SyndicationCandidateChannel) => {
      selectChannel(channel);
    },
    [selectChannel]
  );

  const handleSelectItem = useCallback(
    (item: ChannelItem) => {
      selectItem(item);
    },
    [selectItem]
  );

  const handleNext = useCallback(() => {
    setSubmitError(null);
    setStepIndex((prev) => {
      if (prev === 0 && selectedChannel) return 1;
      if (prev === 1 && selectedItem) return 2;
      return prev;
    });
  }, [selectedChannel, selectedItem]);

  const handleBack = useCallback(() => {
    setSubmitError(null);
    setStepIndex((prev) => (prev === 0 ? 0 : ((prev - 1) as StepIndex)));
  }, []);

  const canGoNext = useMemo(() => {
    if (stepIndex === 0) return !!selectedChannel && !submitting;
    if (stepIndex === 1) return !!selectedItem && !submitting;
    return false;
  }, [stepIndex, selectedChannel, selectedItem, submitting]);

  const isConfigValid = useMemo(() => {
    if (!selectedChannel || !selectedItem) return false;
    if (versionMode === 'pinned') {
      return pinnedVersion !== null && pinnedVersion > 0;
    }
    return true;
  }, [selectedChannel, selectedItem, versionMode, pinnedVersion]);

  const versionOptions = useMemo(() => {
    if (!selectedItem?.item_version) return [];
    const max = selectedItem.item_version;
    return Array.from({ length: max }, (_, i) => {
      const v = max - i;
      return { label: `v${v}`, value: v };
    });
  }, [selectedItem]);

  const handleVersionModeChange = useCallback(
    (mode: VersionMode) => {
      setVersionMode(mode);
      if (mode === 'pinned' && pinnedVersion === null && selectedItem?.item_version) {
        setPinnedVersion(selectedItem.item_version);
      }
    },
    [pinnedVersion, selectedItem]
  );

  const buildParams = useCallback((): SyndicateItemParams | null => {
    if (!selectedChannel || !selectedItem) return null;
    const base: SyndicateItemParams = {
      sourceChannelSystemName: selectedChannel.system_name,
      sourceItemType: selectedItem.item_type,
      sourceItemSystemName: selectedItem.item_system_name,
      versionMode,
    };
    if (versionMode === 'pinned' && pinnedVersion !== null) {
      base.pinnedVersion = pinnedVersion;
    }
    return base;
  }, [selectedChannel, selectedItem, versionMode, pinnedVersion]);

  const handleSubmit = useCallback(async () => {
    if (!isConfigValid) return;
    const params = buildParams();
    if (!params) return;

    setSubmitting(true);
    setSubmitError(null);

    const result = await syndicate(params);
    setSubmitting(false);

    if (result) {
      onSyndicated(result);
      onHide();
    } else {
      setSubmitError(t('marketplace.syndicate.error'));
    }
  }, [isConfigValid, buildParams, syndicate, onSyndicated, onHide, t]);

  const renderStepIndicator = () => (
    <div
      className="marketplace-syndicate-dialog__steps"
      data-testid="syndicate-step-indicator"
    >
      {[0, 1, 2].map((idx) => {
        const isActive = idx === stepIndex;
        const isCompleted = idx < stepIndex;
        const classes = [
          'marketplace-syndicate-dialog__step-dot',
          isActive ? 'marketplace-syndicate-dialog__step-dot--active' : '',
          isCompleted ? 'marketplace-syndicate-dialog__step-dot--completed' : '',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <div key={idx} className={classes}>
            <span className="marketplace-syndicate-dialog__step-number">{idx + 1}</span>
          </div>
        );
      })}
    </div>
  );

  const renderStep1 = () => (
    <div
      className="marketplace-syndicate-dialog__step"
      data-testid="syndicate-step-1"
    >
      <h3 className="marketplace-syndicate-dialog__step-title">
        {t('marketplace.syndicate.step1.title')}
      </h3>
      <div className="marketplace-syndicate-dialog__search">
        <i
          className="pi pi-search marketplace-syndicate-dialog__search-icon"
          aria-hidden="true"
        />
        <input
          type="text"
          className="marketplace-dialog__input marketplace-syndicate-dialog__search-input"
          placeholder={t('marketplace.syndicate.step1.searchChannels')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          data-testid="syndicate-search-input"
        />
      </div>

      <div className="marketplace-syndicate-dialog__list">
        {(() => {
          if (loading && channels.length === 0) {
            return (
              <div className="marketplace-syndicate-dialog__loading">
                <i className="pi pi-spin pi-spinner" aria-hidden="true" />
                <span>{t('marketplace.syndicate.step1.loading')}</span>
              </div>
            );
          }
          if (channels.length === 0) {
            return (
              <div
                className="marketplace-syndicate-dialog__empty"
                data-testid="syndicate-no-channels"
              >
                {t('marketplace.syndicate.step1.noChannels')}
              </div>
            );
          }
          return channels.map((channel) => {
            const isSelected = selectedChannel?.id === channel.id;
            const classes = [
              'marketplace-syndicate-dialog__row',
              isSelected ? 'marketplace-syndicate-dialog__row--selected' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                key={channel.id}
                type="button"
                className={classes}
                onClick={() => handleSelectChannel(channel)}
                data-testid={`syndicate-channel-${channel.id}`}
              >
                <i className="pi pi-megaphone" aria-hidden="true" />
                <div className="marketplace-syndicate-dialog__row-info">
                  <span className="marketplace-syndicate-dialog__row-name">
                    {getLocalizedText(channel.name)}
                  </span>
                  {channel.description &&
                    getLocalizedText(channel.description) && (
                      <span className="marketplace-syndicate-dialog__row-desc">
                        {getLocalizedText(channel.description)}
                      </span>
                    )}
                </div>
              </button>
            );
          });
        })()}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div
      className="marketplace-syndicate-dialog__step"
      data-testid="syndicate-step-2"
    >
      <h3 className="marketplace-syndicate-dialog__step-title">
        {t('marketplace.syndicate.step2.title')}
      </h3>

      <div className="marketplace-syndicate-dialog__list">
        {(() => {
          if (loading && items.length === 0) {
            return (
              <div className="marketplace-syndicate-dialog__loading">
                <i className="pi pi-spin pi-spinner" aria-hidden="true" />
                <span>{t('marketplace.syndicate.step2.loading')}</span>
              </div>
            );
          }
          if (items.length === 0) {
            return (
              <div
                className="marketplace-syndicate-dialog__empty"
                data-testid="syndicate-no-items"
              >
                {t('marketplace.syndicate.step2.noShareableItems')}
              </div>
            );
          }
          return items.map((item) => {
            const name =
              getLocalizedText(item.item_metadata?.name) ||
              item.item_system_name;
            const desc = getLocalizedText(item.item_metadata?.description);
            const isSelected = selectedItem?.id === item.id;
            const classes = [
              'marketplace-syndicate-dialog__row',
              isSelected ? 'marketplace-syndicate-dialog__row--selected' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                key={item.id}
                type="button"
                className={classes}
                onClick={() => handleSelectItem(item)}
                data-testid={`syndicate-item-${item.id}`}
              >
                <i className="pi pi-file" aria-hidden="true" />
                <div className="marketplace-syndicate-dialog__row-info">
                  <span className="marketplace-syndicate-dialog__row-name">
                    {name}
                  </span>
                  {desc && (
                    <span className="marketplace-syndicate-dialog__row-desc">
                      {desc}
                    </span>
                  )}
                </div>
              </button>
            );
          });
        })()}
      </div>
    </div>
  );

  const renderStep3 = () => {
    if (!selectedChannel || !selectedItem) return null;
    const channelLabel = getLocalizedText(selectedChannel.name);
    const itemLabel =
      getLocalizedText(selectedItem.item_metadata?.name) ||
      selectedItem.item_system_name;

    return (
      <div
        className="marketplace-syndicate-dialog__step"
        data-testid="syndicate-step-3"
      >
        <h3 className="marketplace-syndicate-dialog__step-title">
          {t('marketplace.syndicate.step3.title')}
        </h3>

        <fieldset className="marketplace-syndicate-dialog__fieldset">
          <legend className="marketplace-syndicate-dialog__fieldset-legend">
            {t('marketplace.syndicate.step3.versionMode')}
          </legend>
          <label className="marketplace-syndicate-dialog__radio">
            <input
              type="radio"
              name="syndicate-version-mode"
              value="latest"
              checked={versionMode === 'latest'}
              onChange={() => handleVersionModeChange('latest')}
              data-testid="syndicate-mode-latest"
            />
            <span>{t('marketplace.syndicate.step3.alwaysLatest')}</span>
          </label>
          <label className="marketplace-syndicate-dialog__radio">
            <input
              type="radio"
              name="syndicate-version-mode"
              value="pinned"
              checked={versionMode === 'pinned'}
              onChange={() => handleVersionModeChange('pinned')}
              data-testid="syndicate-mode-pinned"
            />
            <span>{t('marketplace.syndicate.step3.pinToVersion')}</span>
          </label>
        </fieldset>

        {versionMode === 'pinned' && (
          <div className="marketplace-syndicate-dialog__field">
            <label
              htmlFor="syndicate-pinned-version"
              className="marketplace-dialog__label"
            >
              {t('marketplace.syndicate.step3.selectVersion')}
            </label>
            <Dropdown
              inputId="syndicate-pinned-version"
              value={pinnedVersion}
              options={versionOptions}
              onChange={(e: DropdownChangeEvent) =>
                setPinnedVersion(e.value as number)
              }
              placeholder={t('marketplace.syndicate.step3.selectVersion')}
              className="marketplace-syndicate-dialog__version-dropdown"
              data-testid="syndicate-version-dropdown"
            />
          </div>
        )}

        <section
          className="marketplace-syndicate-dialog__summary"
          data-testid="syndicate-summary"
        >
          <h4 className="marketplace-syndicate-dialog__summary-title">
            {t('marketplace.syndicate.step3.summary')}
          </h4>
          <dl className="marketplace-syndicate-dialog__summary-list">
            <div className="marketplace-syndicate-dialog__summary-row">
              <dt>{t('marketplace.syndicate.step3.summaryChannel')}</dt>
              <dd>{channelLabel}</dd>
            </div>
            <div className="marketplace-syndicate-dialog__summary-row">
              <dt>{t('marketplace.syndicate.step3.summaryItem')}</dt>
              <dd>{itemLabel}</dd>
            </div>
            <div className="marketplace-syndicate-dialog__summary-row">
              <dt>{t('marketplace.syndicate.step3.summaryVersion')}</dt>
              <dd>
                {(() => {
                  if (versionMode === 'latest') return t('marketplace.syndicate.step3.alwaysLatest');
                  if (pinnedVersion === null) return '';
                  return `v${pinnedVersion}`;
                })()}
              </dd>
            </div>
          </dl>
        </section>
      </div>
    );
  };

  if (!visible) return null;

  return (
    <div
      className="marketplace-dialog-overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="marketplace-dialog marketplace-syndicate-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="syndicate-dialog-title"
      >
        <div className="marketplace-dialog__header">
          <h2 id="syndicate-dialog-title" className="marketplace-dialog__title">
            {t('marketplace.syndicate.title')}
          </h2>
          <button
            type="button"
            className="marketplace-dialog__close-btn"
            onClick={handleClose}
            disabled={submitting}
            aria-label={t('marketplace.syndicate.close')}
          >
            &times;
          </button>
        </div>

        <div className="marketplace-syndicate-dialog__step-header">
          {renderStepIndicator()}
          <span
            className="marketplace-syndicate-dialog__step-label"
            data-testid="syndicate-step-label"
          >
            {t('marketplace.syndicate.stepLabel', {
              current: stepIndex + 1,
              total: 3,
            })}
          </span>
        </div>

        <div className="marketplace-dialog__body marketplace-syndicate-dialog__body">
          {stepIndex === 0 && renderStep1()}
          {stepIndex === 1 && renderStep2()}
          {stepIndex === 2 && renderStep3()}

          {(error || submitError) && (
            <div
              className="marketplace-syndicate-dialog__error"
              data-testid="syndicate-error"
              role="alert"
            >
              <span>{submitError || error}</span>
              {submitError && stepIndex === 2 && (
                <button
                  type="button"
                  className="marketplace-syndicate-dialog__retry-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                  data-testid="syndicate-retry-btn"
                >
                  {t('marketplace.syndicate.retry')}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="marketplace-syndicate-dialog__footer">
          <button
            type="button"
            className="marketplace-syndicate-dialog__back-btn"
            onClick={handleBack}
            disabled={stepIndex === 0 || submitting}
            data-testid="syndicate-back-btn"
          >
            {t('marketplace.syndicate.back')}
          </button>
          <div className="marketplace-syndicate-dialog__footer-spacer" />
          <button
            type="button"
            className="marketplace-syndicate-dialog__cancel-btn"
            onClick={handleClose}
            disabled={submitting}
            data-testid="syndicate-cancel-btn"
          >
            {t('marketplace.syndicate.cancel')}
          </button>
          {stepIndex < 2 ? (
            <button
              type="button"
              className="marketplace-syndicate-dialog__next-btn"
              onClick={handleNext}
              disabled={!canGoNext}
              data-testid="syndicate-next-btn"
            >
              {t('marketplace.syndicate.next')}
            </button>
          ) : (
            <button
              type="button"
              className="marketplace-syndicate-dialog__confirm-btn"
              onClick={handleSubmit}
              disabled={!isConfigValid || submitting}
              data-testid="syndicate-confirm-btn"
            >
              {submitting ? (
                <>
                  <i
                    className="pi pi-spin pi-spinner"
                    aria-hidden="true"
                  />
                  <span>{t('marketplace.syndicate.submitting')}</span>
                </>
              ) : (
                t('marketplace.syndicate.step3.confirm')
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SyndicateItemDialogOrganism;
